// lua_bridge.js: JS wrapper over the Lua game state (M2.c-10a).
//
// LuaGameState wraps a wasmoon engine that's already had the M2 Lua
// modules (`game.state`, `game.cards`, `parity.serialize`) loaded into
// `package.loaded` by the caller. Hot-seat orchestration (M2.c-10b+)
// drives the engine through this class so the UI never touches
// wasmoon directly.
//
// Why a Lua-side facade (_game table) instead of calling state methods
// from JS: wasmoon's `engine.global.get(name)` returns the Lua value
// directly and the proxy caches deeply nested table reads (one of the
// pinned wasmoon gotchas). By exposing a flat `_game` table of plain
// FUNCTIONS, every call routes through Lua and never reads cached
// fields from JS, so we don't trip the proxy stale-read trap.
//
// Why every Lua result comes back as a JSON string: Serialize.encode
// is already canonical (sorted keys, deterministic), is used by the
// snapshot wire format and the parity goldens, and side-steps the
// nested-proxy issue entirely. The JS side does one JSON.parse per
// call.
//
// Snapshot caching: the snapshot value only changes when an action
// has been submitted. Every mutating method (`deal`, `submit_bid`,
// `dealer_discard`, `play_card`, `score_hand`, `reset`) flips
// `_cacheStale = true`; `snapshot()` reparses Lua only when stale or
// when the (viewerSeatId, post_hand_reveal) key changes. This keeps
// the per-render-frame Lua call count flat, synthesis EM2-3 budget
// is ≤5 calls per 100 frames; a steady frame-loop calling snapshot()
// at 60fps hits Lua exactly once per state transition (i.e. zero
// calls while no action has been submitted).

const BOOTSTRAP_LUA = `
  local State     = require('game.state')
  local Bot       = require('game.bot')
  local Serialize = require('parity.serialize')

  -- Singleton namespace the JS bridge talks to. One state per engine;
  -- 'reset' replaces state for Play Again (M2.c-10e).
  _game = {}
  _game.state = nil
  _game.bots = {}  -- per-seat Bot instances; lazy-init in start_bots.
  _game.bot_settings = {}

  function _game.init(stick_the_dealer, game_target)
    _game.state = State.new({
      stick_the_dealer = stick_the_dealer,
      game_target      = game_target,
    })
  end

  -- (Re)create bot instances for every seat. heuristic_disclosure flips
  -- whether the bot returns Pillar 5 annotations alongside actions.
  function _game.start_bots(heuristic_disclosure)
    _game.bot_settings = {heuristic_disclosure = heuristic_disclosure}
    _game.bots = {}
    for seat = 1, 4 do
      _game.bots[seat] = Bot.new(seat, _game.bot_settings)
    end
  end

  function _game.deal(seed)
    _game.state:deal(seed)
  end

  function _game.set_dealer(seat)
    _game.state._public.dealer_seat = seat
  end

  -- M2.c-10d lifecycle helper: keeps current scores + dealer rotation +
  -- bumps hand_index but resets per-hand fields (trump, maker, etc).
  -- The lifecycle hook calls this between hands when game_over is false.
  function _game.next_hand(next_dealer_seat, next_seed)
    local prev = _game.state
    local target = prev._public.game_target
    local std    = prev._public.stick_the_dealer
    local scores = prev._public.scores
    local hand_index = prev._public.hand_index
    _game.state = State.new({stick_the_dealer = std, game_target = target})
    _game.state._public.scores      = scores
    _game.state._public.hand_index  = hand_index + 1
    _game.state._public.dealer_seat = next_dealer_seat
    _game.state:deal(next_seed)
  end

  function _game.legal_bids(seat)
    return Serialize.encode(_game.state:legal_bids(seat))
  end

  function _game.submit_bid(seat, action, suit)
    local bid = {action = action}
    if suit ~= nil and suit ~= '' then bid.suit = suit end
    return Serialize.encode(_game.state:submit_bid(seat, bid))
  end

  function _game.dealer_discard(card_id)
    return Serialize.encode(_game.state:dealer_discard(card_id))
  end

  function _game.legal_plays(seat)
    return Serialize.encode(_game.state:legal_plays(seat))
  end

  function _game.play_card(seat, card_id)
    return Serialize.encode(_game.state:play_card(seat, card_id))
  end

  function _game.score_hand()
    return Serialize.encode(_game.state:score_hand())
  end

  function _game.snapshot(viewer_seat_id, post_hand_reveal)
    -- viewer_seat_id may be JS-null which wasmoon marshals as nil;
    -- snapshot.lua treats nil as "no viewer, no hand reveal".
    return _game.state:snapshot(viewer_seat_id, {post_hand_reveal = post_hand_reveal})
  end

  -- M2.c-10c bot facades. Each accepts the seat to ask, looks up that
  -- seat's bot instance, and returns the JSON-encoded decision. The bot
  -- methods read state._private.hands[seat] directly — visibility-safe
  -- because we never expose the cross-seat view to JS.

  function _game.bot_bid(seat)
    local bot = _game.bots[seat]
    if not bot then return Serialize.encode({error = 'no bot for seat ' .. tostring(seat)}) end
    local decision = bot:bid(_game.state)
    if decision == nil then return Serialize.encode({}) end
    return Serialize.encode(decision)
  end

  function _game.bot_play(seat)
    local bot = _game.bots[seat]
    if not bot then return Serialize.encode({error = 'no bot for seat ' .. tostring(seat)}) end
    local decision = bot:play(_game.state)
    if decision == nil then return Serialize.encode({}) end
    return Serialize.encode(decision)
  end

  function _game.bot_dealer_discard(seat)
    local hand = _game.state._private.hands[seat]
    if not hand or #hand == 0 then return Serialize.encode({error = 'no hand'}) end
    local pool = {}
    for _, c in ipairs(hand) do pool[#pool + 1] = c end
    local up = _game.state._public.up_card
    if up then pool[#pool + 1] = up end
    return Serialize.encode({card_id = Bot.pick_discard(pool, _game.state._public.trump_suit)})
  end

  function _game.bot_think_delay_ms(seat)
    local bot = _game.bots[seat]
    if not bot then return 350 end
    return bot:think_delay_ms()
  end

  function _game.dispose()
    _game.state = nil
    _game.bots = {}
  end
`;

/**
 * @typedef {{
 *   stick_the_dealer?: boolean,
 *   game_target?: number,
 * }} LuaGameStateSettings
 */

/**
 * @typedef {{
 *   ok: boolean,
 *   error?: string,
 *   [k: string]: unknown,
 * }} ActionResult
 */

export class LuaGameStateError extends Error {
  constructor(message, cause) {
    super(message);
    this.name = 'LuaGameStateError';
    if (cause !== undefined) this.cause = cause;
  }
}

// Serialize.encode emits Lua arrays as numeric-keyed objects:
// {"action":"pass"} alone is an object, but {{...},{...},{...}} becomes
// {"1":{...},"2":{...},"3":{...}}. Object.values() turns that back into
// a real JS array. Empty Lua tables ({}) also normalize cleanly to [].
function asArray(obj) {
  if (Array.isArray(obj)) return obj;
  if (obj === null || typeof obj !== 'object') return [];
  return Object.values(obj);
}

// Walk a parsed snapshot and coerce the known-array fields so callers
// get real JS arrays, not numeric-keyed objects.
function normalizeSnapshot(snap) {
  if (!snap) return snap;
  if (snap.bid_history) snap.bid_history = asArray(snap.bid_history);
  if (snap.trick_plays) snap.trick_plays = asArray(snap.trick_plays);
  if (snap.completed_tricks) snap.completed_tricks = asArray(snap.completed_tricks);
  if (snap.hands && typeof snap.hands === 'object') {
    for (const seat of Object.keys(snap.hands)) {
      snap.hands[seat] = asArray(snap.hands[seat]);
    }
  }
  return snap;
}

export class LuaGameState {
  /**
   * Wire the bootstrap facade into the engine and initialize a fresh
   * state instance. Engine must already have game.state + game.cards
   * + parity.serialize available via `require(...)`.
   *
   * @param {import('wasmoon').LuaEngine} engine
   * @param {LuaGameStateSettings} [settings]
   * @returns {Promise<LuaGameState>}
   */
  static async attach(engine, settings = {}) {
    if (!engine) throw new LuaGameStateError('engine is required');
    try {
      await engine.doString(BOOTSTRAP_LUA);
    } catch (err) {
      throw new LuaGameStateError(`bootstrap failed: ${errMsg(err)}`, err);
    }
    const std = settings.stick_the_dealer ?? true;
    const target = settings.game_target ?? 10;
    try {
      await engine.doString(`_game.init(${std}, ${target})`);
    } catch (err) {
      throw new LuaGameStateError(`init failed: ${errMsg(err)}`, err);
    }
    return new LuaGameState(engine);
  }

  /** @param {import('wasmoon').LuaEngine} engine */
  constructor(engine) {
    this._engine = engine;
    /** @type {any} */
    this._game = engine.global.get('_game');
    if (!this._game) {
      throw new LuaGameStateError('_game facade not present — call attach() first');
    }
    /** @type {object|null} */
    this._cache = null;
    /** @type {string|null} */
    this._cacheKey = null;
    this._cacheStale = true;
    this._luaCallCount = 0;
    this._disposed = false;
  }

  _invalidate() { this._cacheStale = true; }

  _checkDisposed() {
    if (this._disposed) throw new LuaGameStateError('LuaGameState already disposed');
  }

  /**
   * Deal a fresh hand using `seed` (deterministic). Mutating, clears
   * the snapshot cache.
   * @param {number} seed
   */
  deal(seed) {
    this._checkDisposed();
    this._game.deal(seed);
    this._luaCallCount++;
    this._invalidate();
  }

  /**
   * Override the dealer seat before calling deal(). Mutating,
   * also clears the cache.
   * @param {1|2|3|4} seat
   */
  setDealer(seat) {
    this._checkDisposed();
    this._game.set_dealer(seat);
    this._luaCallCount++;
    this._invalidate();
  }

  /**
   * Read-only: returns the list of legal bid options for `seat`.
   * Returns [] when it's not `seat`'s turn (or the state isn't a bid
   * phase).
   * @param {1|2|3|4} seat
   * @returns {Array<{action: string, suit?: string}>}
   */
  legalBids(seat) {
    this._checkDisposed();
    const json = this._game.legal_bids(seat);
    this._luaCallCount++;
    return asArray(JSON.parse(json));
  }

  /**
   * Submit `bid` for `seat`. Mutating.
   * @param {1|2|3|4} seat
   * @param {{action: string, suit?: string}} bid
   * @returns {ActionResult}
   */
  submitBid(seat, bid) {
    this._checkDisposed();
    const json = this._game.submit_bid(seat, bid.action, bid.suit || '');
    this._luaCallCount++;
    this._invalidate();
    return JSON.parse(json);
  }

  /**
   * Dealer's discard after order-up. Mutating.
   * @param {string} cardId
   * @returns {ActionResult}
   */
  dealerDiscard(cardId) {
    this._checkDisposed();
    const json = this._game.dealer_discard(cardId);
    this._luaCallCount++;
    this._invalidate();
    return JSON.parse(json);
  }

  /**
   * Read-only: card IDs the seat is legally allowed to play this trick.
   * @param {1|2|3|4} seat
   * @returns {string[]}
   */
  legalPlays(seat) {
    this._checkDisposed();
    const json = this._game.legal_plays(seat);
    this._luaCallCount++;
    return asArray(JSON.parse(json));
  }

  /**
   * Play a card. Mutating.
   * @param {1|2|3|4} seat
   * @param {string} cardId
   * @returns {ActionResult}
   */
  playCard(seat, cardId) {
    this._checkDisposed();
    const json = this._game.play_card(seat, cardId);
    this._luaCallCount++;
    this._invalidate();
    return JSON.parse(json);
  }

  /**
   * Score the completed hand. Mutating (writes score_summary into
   * _public).
   * @returns {ActionResult}
   */
  scoreHand() {
    this._checkDisposed();
    const json = this._game.score_hand();
    this._luaCallCount++;
    this._invalidate();
    return JSON.parse(json);
  }

  /**
   * Get a visibility-filtered snapshot. CACHED, calls Lua only on
   * the first call after a mutation or when (viewerSeatId,
   * post_hand_reveal) differs from the cached key.
   *
   * @param {1|2|3|4|null} [viewerSeatId]
   * @param {{post_hand_reveal?: boolean}} [opts]
   * @returns {object}
   */
  snapshot(viewerSeatId = null, opts = {}) {
    this._checkDisposed();
    const postReveal = !!opts.post_hand_reveal;
    const viewerKey = viewerSeatId === null ? 'null' : String(viewerSeatId);
    const key = `${viewerKey}|${postReveal ? '1' : '0'}`;
    if (!this._cacheStale && this._cacheKey === key && this._cache) {
      return this._cache;
    }
    const json = this._game.snapshot(viewerSeatId, postReveal);
    this._luaCallCount++;
    const parsed = normalizeSnapshot(JSON.parse(json));
    this._cache = parsed;
    this._cacheKey = key;
    this._cacheStale = false;
    return parsed;
  }

  /**
   * Replace state in-place with a fresh instance. Used for Play Again
   * (M2.c-10e). Mutating.
   * @param {LuaGameStateSettings} [settings]
   */
  reset(settings = {}) {
    this._checkDisposed();
    const std = settings.stick_the_dealer ?? true;
    const target = settings.game_target ?? 10;
    this._game.init(std, target);
    this._luaCallCount++;
    this._cache = null;
    this._cacheKey = null;
    this._cacheStale = true;
  }

  /**
   * Initialize bot instances (one per seat). Call once after attach()
   * before relying on botBid / botPlay. Re-call to flip the disclosure
   * setting at runtime.
   * @param {{ heuristic_disclosure?: boolean }} [opts]
   */
  startBots(opts = {}) {
    this._checkDisposed();
    this._game.start_bots(!!opts.heuristic_disclosure);
    this._luaCallCount++;
  }

  /**
   * Ask the bot at `seat` for a bidding decision. Read-only (does NOT
   * mutate state), caller submits the decision via submitBid().
   * Returns {} when the bot has nothing to say (not its turn / not a
   * bidding state).
   * @param {1|2|3|4} seat
   * @returns {{ action?: string, suit?: string, annotation?: string }}
   */
  botBid(seat) {
    this._checkDisposed();
    const json = this._game.bot_bid(seat);
    this._luaCallCount++;
    return JSON.parse(json);
  }

  /**
   * Ask the bot at `seat` for a play decision. Read-only, caller
   * submits via playCard(). Returns {} when not the bot's turn.
   * @param {1|2|3|4} seat
   * @returns {{ card_id?: string, annotation?: string }}
   */
  botPlay(seat) {
    this._checkDisposed();
    const json = this._game.bot_play(seat);
    this._luaCallCount++;
    return JSON.parse(json);
  }

  /**
   * Ask the bot at `seat` which card to discard. Used when the bot is
   * the dealer + an order_up was made. Read-only.
   * @param {1|2|3|4} seat
   * @returns {{ card_id?: string, error?: string }}
   */
  botDealerDiscard(seat) {
    this._checkDisposed();
    const json = this._game.bot_dealer_discard(seat);
    this._luaCallCount++;
    return JSON.parse(json);
  }

  /**
   * Stochastic think-delay (200..600ms per M2.b). Each call re-rolls.
   * @param {1|2|3|4} seat
   * @returns {number}
   */
  botThinkDelayMs(seat) {
    this._checkDisposed();
    const ms = this._game.bot_think_delay_ms(seat);
    this._luaCallCount++;
    return typeof ms === 'number' ? ms : Number(ms);
  }

  /**
   * Advance to the next hand: preserves running scores, bumps
   * hand_index, rotates dealer, and deals with the new seed. Used by
   * M2.c-10d lifecycle.
   * @param {1|2|3|4} nextDealerSeat
   * @param {number} nextSeed
   */
  nextHand(nextDealerSeat, nextSeed) {
    this._checkDisposed();
    this._game.next_hand(nextDealerSeat, nextSeed);
    this._luaCallCount++;
    this._invalidate();
  }

  /** Number of Lua function calls so far, exposed for tests + perf budgets. */
  getLuaCallCount() { return this._luaCallCount; }

  /** Snapshot cache state, exposed for tests. */
  isCacheStale() { return this._cacheStale; }

  /** Drop the Lua state reference; caller owns the engine. */
  dispose() {
    if (this._disposed) return;
    try { this._game.dispose(); } catch { /* engine may be closed already */ }
    this._game = null;
    this._cache = null;
    this._disposed = true;
  }
}

function errMsg(err) {
  if (err instanceof Error) return err.message;
  return String(err);
}

export const _internals = { BOOTSTRAP_LUA };
